import { isValidUUID } from 'shared/utils/uuid'

const TRACKED_MEETING_PATH_RE = /^\/app\/(?:tracked_meetings|external_tracked_meeting)\/([^/]+)/
const TRACKED_MEETING_ID_RE = /^[\w-]+$/i
const SAMPLE_TRACKED_MEETING_ID = 'sample'

export const getTrackedMeetingUuidFromPathname = (pathname) => {
  const match = pathname.match(TRACKED_MEETING_PATH_RE)
  return match?.[1] ?? null
}

/** Recap page URLs use UUIDs and legacy tracked-meeting ids; thread ids stay RFC UUIDs. */
export const isValidTrackedMeetingId = (value) => (
  typeof value === 'string'
  && value.length > 0
  && TRACKED_MEETING_ID_RE.test(value)
)

/** Email / Callie deep links attach only RFC UUID meetings (never sample). */
export const isAttachableRecapMeetingId = (value) => (
  isValidUUID(value) && value !== SAMPLE_TRACKED_MEETING_ID
)

/** Pane props round-trip through URL query encoding as the string "true". */
export const isAttachRecapContextEnabled = (value) => (
  value === true || value === 'true'
)

export const trackedMeetingUuidsMatch = (left, right) => {
  if (!left || !right) {
    return false
  }
  return left.toLowerCase() === right.toLowerCase()
}

const resolveTrackedMeetingUuidForRecapContext = (paneUuid, pathnameUuid) => {
  if (paneUuid && pathnameUuid && !trackedMeetingUuidsMatch(pathnameUuid, paneUuid)) {
    return undefined
  }

  return paneUuid ?? pathnameUuid
}

/** Pane props that attach/clear recap context; keep in sync with right-rail remount ignores. */
export const RECAP_PANE_PROP_KEYS = [
  'attachRecapContext',
  'trackedMeetingUuid',
  'trackedMeetingTitle',
]

export const stripRecapPaneProps = (paneProps = {}) => {
  const rest = { ...paneProps }
  RECAP_PANE_PROP_KEYS.forEach((key) => {
    delete rest[key]
  })
  return rest
}

export const buildRecapAttachmentPanePropsFromPathname = (pathname) => {
  const trackedMeetingUuid = getTrackedMeetingUuidFromPathname(pathname)
  if (
    !isValidTrackedMeetingId(trackedMeetingUuid)
    || trackedMeetingUuid === SAMPLE_TRACKED_MEETING_ID
  ) {
    return null
  }

  return {
    attachRecapContext: true,
    trackedMeetingUuid,
  }
}

export const mergeRecapPanePropsFromPathname = (
  pathname,
  paneProps = {},
  isRecapIntegrationEnabled = false,
) => {
  if (!isRecapIntegrationEnabled) {
    return paneProps
  }

  const recapProps = buildRecapAttachmentPanePropsFromPathname(pathname)
  if (!recapProps) {
    return paneProps
  }

  if (isAttachRecapContextEnabled(paneProps.attachRecapContext)) {
    const nextUuid = recapProps.trackedMeetingUuid
    const { trackedMeetingTitle, ...rest } = paneProps
    const keepExistingTitle = (
      !!trackedMeetingTitle
      && !!paneProps.trackedMeetingUuid
      && trackedMeetingUuidsMatch(paneProps.trackedMeetingUuid, nextUuid)
    )

    return {
      ...rest,
      attachRecapContext: true,
      trackedMeetingUuid: nextUuid,
      ...(keepExistingTitle ? { trackedMeetingTitle } : {}),
    }
  }

  return { ...paneProps, ...recapProps }
}

export const getAttachedRecapMeetingUuid = (paneProps = {}, pathname) => {
  if (!isAttachRecapContextEnabled(paneProps.attachRecapContext)) {
    return null
  }

  const pathnameUuid = getTrackedMeetingUuidFromPathname(pathname)
  const paneUuid = isValidTrackedMeetingId(paneProps.trackedMeetingUuid)
    ? paneProps.trackedMeetingUuid
    : undefined
  const validPathnameUuid = (
    isValidTrackedMeetingId(pathnameUuid)
    && pathnameUuid !== SAMPLE_TRACKED_MEETING_ID
  )
    ? pathnameUuid
    : undefined

  if (!validPathnameUuid) {
    return null
  }

  return resolveTrackedMeetingUuidForRecapContext(paneUuid, validPathnameUuid) ?? null
}

export const buildRecapContextFromPaneAndPathname = ({
  paneProps = {},
  pathname,
  isRecapIntegrationEnabled = false,
  buildAttachment,
}) => {
  if (!isRecapIntegrationEnabled || !isAttachRecapContextEnabled(paneProps.attachRecapContext)) {
    return null
  }

  const trackedMeetingUuid = getAttachedRecapMeetingUuid(paneProps, pathname)
  if (!trackedMeetingUuid) {
    return null
  }

  // Only keep pane titles that belong to the resolved meeting id. A title can linger after
  // pathname uuid sync when attach was already enabled for a different recap.
  const trackedMeetingTitle = (
    paneProps.trackedMeetingUuid
    && trackedMeetingUuidsMatch(paneProps.trackedMeetingUuid, trackedMeetingUuid)
  ) ? paneProps.trackedMeetingTitle : undefined

  return {
    trackedMeetingUuid,
    trackedMeetingTitle,
    defaultAttachments: [buildAttachment(trackedMeetingUuid, trackedMeetingTitle)],
  }
}
